import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import TopHeadding from "../../components/layout/TopHeadding";
import { Dropdown } from "react-bootstrap";
import { fetchFormulaById, createFormula, updateFormula, resetStatus } from "../../store/slices/formulaSlice";
import AddVariablePopup from "../../components/popups/AddVariablePopup";
import { fetchAllVariables } from "../../store/slices/variablesSlice";
import StatusPopup from "../../components/popups/StatusPopup";

// Icons Imports
import opretor_plus_icon from "../../assets/images/opretor_plus_icon.svg";
import opretor_minus_icon from "../../assets/images/opretor_minus_icon.svg";
import opretor_star_icon from "../../assets/images/opretor_star_icon.svg";
import opretor_devide_icon from "../../assets/images/opretor_devide_icon.svg";
import opretor_percentage_icon from "../../assets/images/opretor_percentage_icon.svg";
import opretor_equal_icon from "../../assets/images/opretor_equal_icon.svg";
import opretor_lesser_icon from "../../assets/images/opretor_lesser_icon.svg";
import opretor_gretor_icon from "../../assets/images/opretor_gretor_icon.svg";
import backspace_icon from "../../assets/images/backspace_icon.svg";

const AddNewFormula = ({ onApplyFormula }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [formulaName, setFormulaName] = useState("");
  const [formulaParts, setFormulaParts] = useState([]);
  const [result, setResult] = useState(null);
  const [taxApply, setTaxApply] = useState("Select");
  const [isLoading, setIsLoading] = useState(false);

  const { id } = location.state || {};

  const AddFormulaGetVariableKey = "variables"

  const { user } = useSelector(state => state.user);

  const variables = useSelector(state => state.variable.listDataByKey[AddFormulaGetVariableKey] || []);
  const loading = useSelector(state => state.variable.listLoadingByKey[AddFormulaGetVariableKey] || false);
  const { isSuccess, errorMessage, selectedFormula } = useSelector(state => state.formula);

  const [showAddVarPopup, setShowAddVarPopup] = useState(false);
  const [variableValues, setVariableValues] = useState({});
  const OPERATORS = useMemo(() => (
    ["+", "-", "*", "/", "%", "<", ">", "==", "(", ")", "If", ","]
  ), []);

  useEffect(() => {
    dispatch(resetStatus());
  }, [dispatch]);

  useEffect(() => {
    if (isSuccess) {
      setIsLoading(false);
      dispatch(resetStatus());
      navigate("/formula", {
        replace: true,
      });
    }
  }, [isSuccess, dispatch, navigate]);

  useEffect(() => {
    setShowAddVarPopup(false)
  }, [errorMessage]);

  useEffect(() => {
    dispatch(fetchAllVariables({ params: {}, key: AddFormulaGetVariableKey }));
  }, [dispatch]);

  useEffect(() => {
    if (id) {
      dispatch(fetchFormulaById(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (selectedFormula && id) {
      setFormulaName(selectedFormula.name || "");
      setFormulaParts(selectedFormula.expression ? selectedFormula.expression.split(/\s+/) : []);
      setResult(selectedFormula.description || "");
      setTaxApply(selectedFormula.taxApply || "Select");

      // Parse description to set variableValues for edit mode
      if (selectedFormula.description) {
        const lines = selectedFormula.description.split('\n');
        const newVariableValues = {};
        lines.forEach(line => {
          if (line.includes(':')) {
            const [varName, varValue] = line.split(':').map(s => s.trim());
            if (varName && varValue !== undefined) {
              newVariableValues[varName] = Number(varValue);
            }
          }
        });
        setVariableValues(newVariableValues);
      }
    }
  }, [selectedFormula, id]);

  const isNumberPart = (v) => /^[0-9.]$/.test(v);

  const addToFormula = React.useCallback((value) => {
    setFormulaParts(prev => {
      const last = prev[prev.length - 1];

      if (isNumberPart(value) && last && /^[0-9.]+$/.test(last)) {
        if (value === "." && last.includes(".")) return prev;
        return [...prev.slice(0, -1), last + value];
      }

      if (value === "." && (!last || OPERATORS.includes(last))) {
        return [...prev, "0."];
      }

      return [...prev, value];
    });
  }, [OPERATORS]);

  // eslint-disable-next-line no-new-func
  const runFormula = () => {
    try {
      const missing = getVariablesFromFormula()
        .filter(v => variableValues[v] === undefined);

      if (missing.length) {
        setResult(`Please enter value for: ${missing.join(", ")}`);
        return;
      }

      let res;
      let fullExp = formulaParts.join("");
      if (fullExp.includes("If")) {
        // Handle If condition: If(condition, trueValue, falseValue)
        const ifStart = fullExp.indexOf("If(");
        if (ifStart !== -1) {
          const openParen = ifStart + 2; // after If(
          let parenCount = 1;
          let closeParen = openParen;
          for (let i = openParen + 1; i < fullExp.length; i++) {
            if (fullExp[i] === '(') parenCount++;
            else if (fullExp[i] === ')') parenCount--;
            if (parenCount === 0) {
              closeParen = i;
              break;
            }
          }
          const ifContent = fullExp.substring(openParen + 1, closeParen);
          const parts = ifContent.split(',').map(s => s.trim());
          if (parts.length === 3) {
            let conditionStr = parts[0].trim();
            let trueStr = parts[1].trim();
            let falseStr = parts[2].trim();

            // Insert missing operators
            if (!conditionStr.includes('>') && !conditionStr.includes('<') && !conditionStr.includes('==') && conditionStr.match(/\w+\s+\d+/)) {
              conditionStr = conditionStr.replace(/(\w+)\s+(\d+)/, '$1 > $2');
            }
            if (!falseStr.includes('*') && !falseStr.includes('/') && !falseStr.includes('+') && !falseStr.includes('-') && falseStr.match(/\w+\s+\([^)]+\)/)) {
              falseStr = falseStr.replace(/(\w+)\s+\(([^)]+)\)/, '$1 * ($2)');
              falseStr = falseStr.replace(/\((\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)\)/, '($1 / $2)');
            }

            // Replace variables
            const conditionExp = conditionStr.replace(/\b\w+\b/g, match => variableValues[match] ?? match);
            // eslint-disable-next-line no-new-func
            const condition = Function(`"use strict"; return (${conditionExp})`)();

            if (condition) {
              const trueExp = trueStr.replace(/\b\w+\b/g, match => variableValues[match] ?? match);
              // eslint-disable-next-line no-new-func
              res = Function(`"use strict"; return (${trueExp})`)()
            } else {
              const falseExp = falseStr.replace(/\b\w+\b/g, match => variableValues[match] ?? match);
              // eslint-disable-next-line no-new-func
              res = Function(`"use strict"; return (${falseExp})`)()
            }
          } else {
            throw new Error("Invalid If syntax");
          }
        } else {
          throw new Error("Invalid If syntax");
        }
      } else {
        const evalExp = fullExp
          .split(/\s+/)
          .map(p => variableValues[p] ?? p)
          .join("");
        // eslint-disable-next-line no-new-func
        res = Function(`"use strict"; return (${evalExp})`)();
      }

      // Display just the calculated result value
      setResult(`${res}`);
    } catch (e) {
      setResult("Invalid formula");
    }
  };

  const handleAddVariable = () => {
    dispatch(fetchAllVariables({ params: {}, key: AddFormulaGetVariableKey }));
    setShowAddVarPopup(false);
  };

  const handleApply = async () => {
    if (!formulaName.trim() || !formulaParts.length) return;

    // Set loading state
    setIsLoading(true);

    const code = formulaName.split(' ').map(word => word.charAt(0).toUpperCase()).join('');
    const payload = {
      userId: user.id,
      code,
      name: formulaName,
      expression: formulaParts.join(" "),
      description: result || "",
      taxApply,
      isActive: true,
      variables: getVariablesFromFormula().map(name => {
        const varObj = variables.find(v => v.name === name);
        return varObj ? { id: varObj.id, name: varObj.name } : null;
      }).filter(Boolean)
    };

    try {
      if (id) {
        await dispatch(updateFormula({ id: id, data: payload }));
      } else {
        await dispatch(createFormula(payload));
      }
      onApplyFormula?.(payload);
    } catch (error) {
      setIsLoading(false);
    }
  };

  const handleBackspace = React.useCallback(() => {
    setFormulaParts(prev => {
      if (!prev.length) return prev;

      // Convert full formula to string
      const fullFormula = prev.join(" ");

      // Remove last character
      const updatedFormula = fullFormula.slice(0, -1).trimEnd();

      if (!updatedFormula) return [];

      // Split again properly
      return updatedFormula.split(/\s+/);
    });
  }, []);

  // Handle click on a formula part to remove it
  // This function is kept for potential future use
  // eslint-disable-next-line no-unused-vars
  const _handleFormulaPartClick = (indexToRemove) => {
    setFormulaParts(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  // Handle keyboard events for numbers and operators
  useEffect(() => {
    const handleKeyDown = (e) => {

      // Ignore typing inside input, textarea, select
      const tag = e.target.tagName.toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") {
        return;
      }

      const key = e.key;

      if (key === "Backspace") {
        handleBackspace();
        return;
      }

      if (/^[0-9.]$/.test(key)) {
        addToFormula(key);
        return;
      }

      const operatorMap = {
        '+': '+',
        '-': '-',
        '*': '*',
        '/': '/',
        '%': '%',
        '=': '==',
        '(': '(',
        ')': ')',
        ',': ',',
        '<': '<',
        '>': '>'
      };

      if (operatorMap[key]) {
        addToFormula(operatorMap[key]);
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [addToFormula, handleBackspace]);


  const handleClear = () => {
    setFormulaParts([]);
    setResult(null);
    setVariableValues({});
  };

  const getVariablesFromFormula = () => {
    const fullExp = formulaParts.join("");
    const matches = fullExp.match(/\b[A-Za-z_][A-Za-z0-9_]*\b/g) || [];
    return [...new Set(matches.filter(word => !OPERATORS.includes(word) && isNaN(word)))];
  };

  const handleVarValueChange = (name, value) => {
    setVariableValues(prev => ({
      ...prev,
      [name]: Number(value)
    }));
  };

  return (
    <div className="add_new_formula">
      <TopHeadding title={id ? "Edit Formula" : "Add New Formula"} showDatepicker={false} />

      <div className="create_formulas mt_40">
        <div className="container-fluid">
          <div className="row">

            {/* LEFT SIDE: INPUTS & OPERATORS */}
            <div className="col-md-5">
              <div className="white_box px_32 py_20">
                <div className="common_input_forms">

                  {/* Formula Name */}
                  <div className="form_group">
                    <label>Formula Name</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter Formula Name"
                      value={formulaName}
                      onChange={(e) => setFormulaName(e.target.value)}
                    />
                  </div>

                  {/* Tax Apply Dropdown */}
                  <div className="form_group">
                    <label>Tax Apply</label>
                    <Dropdown>
                      <Dropdown.Toggle className="select_formula" variant="primary">
                        {taxApply}
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        {["PostTax", "PreTax"].map((t) => (
                          <Dropdown.Item key={t} onClick={() => setTaxApply(t)}>{t}</Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>

                  {/* Variables - Old CSS class used here */}
                  <div className="varibales mt_20">
                    <h5>Input Variables</h5>
                    <div className="d-flex mt_10 gap_14 flex-wrap">
                      {loading ? (
                        <span>Loading variables</span>
                      ) : (
                        variables.map((v) => (
                          // eslint-disable-next-line jsx-a11y/anchor-is-valid
                          <a
                            key={v.id}
                            href="#"
                            onClick={(e) => { e.preventDefault(); addToFormula(v.name); }}
                            className={`varibale_badge ${formulaParts.includes(v.name) ? "active_variable" : ""}`}
                          >
                            {v.name}
                          </a>
                        ))
                      )}
                      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                      <a href="#" className="btn_link" onClick={(e) => { e.preventDefault(); setShowAddVarPopup(true); }}>+ Add Variable</a>
                    </div>
                  </div>

                  {/* Operators - Old CSS structure with 2 groups */}
                  <div className="operators mt_20">
                    <h5>Operators</h5>
                    <div className="d-flex gap_14 mt_10 flex-wrap">
                      <div className="operators_items">
                        <div className="icon" onClick={() => addToFormula("+")}><img src={opretor_plus_icon} alt="+" /></div>
                        <div className="icon" onClick={() => addToFormula("-")}><img src={opretor_minus_icon} alt="-" /></div>
                        <div className="icon" onClick={() => addToFormula("*")}><img src={opretor_star_icon} alt="*" /></div>
                      </div>
                      <div className="operators_items">
                        <div className="icon" onClick={() => addToFormula("/")}><img src={opretor_devide_icon} alt="/" /></div>
                        <div className="icon" onClick={() => addToFormula("%")}><img src={opretor_percentage_icon} alt="%" /></div>
                        <div className="icon" onClick={() => addToFormula("==")}><img src={opretor_equal_icon} alt="=" /></div>
                      </div>
                      <div className="operators_items">
                        <div className="icon" onClick={() => addToFormula(">")}><img src={opretor_gretor_icon} alt=">" /></div>
                        <div className="icon" onClick={() => addToFormula("<")}><img src={opretor_lesser_icon} alt="<" /></div>
                      </div>
                      <div className="operators_items">
                        <div className="icon" onClick={() => addToFormula("(")}>
                          (
                        </div>
                        <div className="icon" onClick={() => addToFormula(")")}>
                          )
                        </div>
                        <div className="icon" onClick={() => addToFormula("If")}>
                          If
                        </div>
                        <div className="icon" onClick={() => addToFormula(",")}>
                          ,

                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="operators mt_20">
                    <h5>Numbers</h5>
                    <div className="d-flex gap-10 flex-wrap">
                      <div className="operators_items">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 0].map(n => (
                          <div
                            key={n}
                            className="icon number-icon"
                            onClick={() => addToFormula(String(n))}
                          >
                            {n}
                          </div>
                        ))}
                        <div className="icon number-icon" onClick={() => addToFormula(".")}>.</div>
                      </div>
                    </div>
                  </div>

                  {/* Tax Input */}
                  {/* <div className="form_group mt_20">
                    <label>Tax</label>
                    <input
                      type="number"
                      className="form-control"
                      placeholder="Enter Tax"
                      onChange={(e) => handleVarValueChange("Tax", e.target.value)}
                    />
                  </div> */}

                  {/* Action Buttons - Clear, Delete, Run */}
                  <div className="d-flex w-100 mt_40 gap-2">
                    <button className="btn btn_secondary" onClick={handleClear}>
                      Clear All
                    </button>
                    <button className="btn btn_outline_primary" onClick={handleBackspace}>
                      <img src={backspace_icon} alt="backspace" />
                      Backspace
                    </button>
                    <button className="btn btn_primary d-inline-block ms-auto" onClick={runFormula}>
                      Run Formula
                    </button>
                  </div>

                </div>
              </div>
            </div>

            {/* RIGHT SIDE: FORMULA DISPLAY & RESULT */}
            <div className="col-md-7">
              <div className="white_box px_32 py_20">
                <p>Formula</p>
                <div className="white_box mt_20" style={{ padding: "10px" }}>
                  <h5>Test Values</h5>

                  <div className="d-flex flex-wrap gap-3">
                    {getVariablesFromFormula().map(v => (
                      <div key={v} className="variable_input_box" style={{ width: "200px" }}>
                        <label>{v}</label>
                        <input
                          type="number"
                          className="form-control"
                          value={variableValues[v] || ''}
                          onChange={(e) => handleVarValueChange(v, e.target.value)}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <pre className="selected_valeus mt_20" style={{ overflow: 'auto' }}>{formulaParts.join(" ")}</pre>
              </div>

              <div className="white_box px_32 py_20 mt_20 run_formula_result d-flex flex-column">
                <div className="results" style={{ minHeight: '150px' }}>
                  {result && <pre>{result}</pre>}
                </div>
                <div className="d-flex w-100 mt-auto pt_20">
                  <button className="btn btn_secondary d-inline-block ms-auto me-3" onClick={() => navigate("/formula")}>
                    Cancel
                  </button>
                  <button className="btn btn_primary d-inline-block" onClick={handleApply} disabled={isLoading}>
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Applying
                      </>
                    ) : (
                      "Apply"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ADD VARIABLE POPUP */}
      <AddVariablePopup
        show={showAddVarPopup}
        onClose={() => setShowAddVarPopup(false)}
        onAddVariable={(handleAddVariable)}
      />

      <StatusPopup
        show={errorMessage !== null}
        type="error"
        title="Error"
        message={errorMessage}
        handleClose={() => dispatch(resetStatus())}
      />
    </div>
  );
};

export default AddNewFormula;
